import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight } from "lucide-react";

const values = [
  {
    title: "Qualité",
    description: "Des équipements professionnels et des procédés maîtrisés pour un résultat impeccable."
  },
  {
    title: "Conseil",
    description: "Un accompagnement personnalisé de la conception à la livraison de votre projet."
  },
  {
    title: "Réactivité",
    description: "Des délais optimisés et une communication transparente sur l'avancement."
  },
  {
    title: "Créativité",
    description: "Une équipe créative pour donner vie à vos idées les plus originales."
  }
];

const expertise = [
  "Impression DTF sur textiles (t-shirts, sacs, vêtements)",
  "Sublimation sur tasses, gourdes et objets",
  "Sérigraphie pour grandes séries",
  "Flocage et flex pour marquage textile",
  "Gravure et plaques personnalisées",
  "Conception graphique et infographie",
  "Affiches, kakémonos, cartes de visite",
  "Badges et supports publicitaires"
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-secondary/30" data-testid="about-hero">
        <div className="container-custom">
          <motion.div {...fadeInUp}>
            <p className="eyebrow mb-3">Qui sommes-nous</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="about-title">
              À propos
            </h1>
            <p className="text-muted text-lg max-w-2xl">
              Un atelier de sérigraphie, d'infographie et d'imprimerie au service de vos projets en Martinique.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding" data-testid="about-story">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                L'atelier qui donne vie à vos idées
              </h2>
              <div className="space-y-4 text-muted">
                <p>
                  <strong className="text-foreground">ART PRINT</strong> est un atelier de sérigraphie, 
                  d'infographie et d'imprimerie basé à Fort-de-France en Martinique.
                </p>
                <p>
                  Notre mission : transformer vos projets en réalisations concrètes, 
                  avec la qualité et la créativité que vous méritez. Nous imprimons sur 
                  tous types de supports : t-shirts, tasses, gourdes, casquettes, sacs, 
                  plexiglass et bien plus encore.
                </p>
                <p>
                  Spécialisés en impression DTF, sublimation, sérigraphie et infographie, 
                  nous accompagnons particuliers et professionnels dans leurs projets de 
                  personnalisation, communication visuelle et événementiel.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/3] bg-secondary overflow-hidden">
                <img 
                  src="/images/realisations/real-10.jpeg"
                  alt="Atelier ART PRINT"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-foreground flex flex-col items-center justify-center text-background">
                <span className="text-3xl font-bold">972</span>
                <span className="text-xs">Martinique</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-secondary/30" data-testid="about-values">
        <div className="container-custom">
          <motion.div className="mb-12" {...fadeInUp}>
            <p className="eyebrow mb-3">Notre philosophie</p>
            <h2 className="text-3xl md:text-4xl font-bold">Nos valeurs</h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, i) => (
              <motion.div
                key={value.title}
                className="border-l-2 border-foreground pl-6"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <h3 className="font-bold text-xl mb-2">{value.title}</h3>
                <p className="text-muted text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="section-padding" data-testid="about-expertise">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-square bg-secondary overflow-hidden">
                  <img 
                    src="/images/realisations/real-1.jpeg"
                    alt="Textile"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square bg-secondary overflow-hidden">
                  <img 
                    src="/images/realisations/real-3.jpeg"
                    alt="Gravure"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square bg-secondary overflow-hidden">
                  <img 
                    src="/images/realisations/real-4.jpeg"
                    alt="Sublimation"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square bg-secondary overflow-hidden">
                  <img 
                    src="/images/realisations/real-15.jpeg"
                    alt="Impression"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>
            
            <motion.div className="order-1 lg:order-2" {...fadeInUp}>
              <p className="eyebrow mb-3">Savoir-faire</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Notre expertise</h2>
              <p className="text-muted mb-8">
                Une maîtrise complète des techniques d'impression et de personnalisation 
                pour répondre à tous vos besoins, du plus simple au plus technique.
              </p>
              <ul className="space-y-3">
                {expertise.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-[hsl(var(--accent-green))] flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="section-padding bg-foreground text-background" data-testid="about-location">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <p className="eyebrow text-background/50 mb-3">Localisation</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Notre atelier</h2>
              <p className="text-background/70 mb-8">
                Situé à Fort-de-France en Martinique, notre atelier vous accueille pour 
                discuter de vos projets, voir des échantillons ou récupérer vos commandes.
              </p>
              <div className="space-y-4 mb-8">
                <div>
                  <p className="font-bold">Adresse</p>
                  <p className="text-background/70">97200 Fort-de-France, Martinique</p>
                </div>
                <div>
                  <p className="font-bold">Horaires</p>
                  <p className="text-background/70">Lundi - Vendredi : 8h - 18h</p>
                </div>
                <div>
                  <p className="font-bold">Téléphone</p>
                  <p className="text-background/70">0696 60 13 17</p>
                </div>
                <div>
                  <p className="font-bold">Responsable</p>
                  <p className="text-background/70">DINGEVAL Guetty</p>
                </div>
              </div>
              <Link 
                to="/devis" 
                className="btn-outline border-background text-background hover:bg-background hover:text-foreground inline-flex items-center gap-2"
                data-testid="about-cta-devis"
              >
                Discuter de mon projet
                <ArrowRight size={16} />
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-video bg-background/10 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-6xl font-bold mb-2">972</p>
                  <p className="text-background/50">Fort-de-France, Martinique</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding" data-testid="about-cta">
        <div className="container-custom text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Prêt à démarrer votre projet ?
            </h2>
            <p className="text-muted mb-8 max-w-lg mx-auto">
              Contactez-nous pour une consultation gratuite et un devis personnalisé.
            </p>
            <Link to="/devis" className="btn-primary" data-testid="about-final-cta">
              Demander un devis
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
